import { Linkedin } from "lucide-react";
import { useApp } from "@/context/AppProviders";

const Footer = () => {
  const { t, setTermsOpen } = useApp();
  const f = t.footer;

  const scrollTo = (e, href) => {
    e.preventDefault();
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  const product = [
    { href: "#features", label: f.links.features },
    { href: "#pricing", label: f.links.pricing },
    { href: "#activation", label: f.links.activation },
  ];
  const resources = [
    { href: "#downloads", label: f.links.downloads },
    { href: "#downloads", label: f.links.manual },
    { href: "#contact", label: f.links.contact },
  ];
  const legal = [
    { label: f.links.privacy, href: "#compliance" },
    { label: t.terms.title, action: () => setTermsOpen(true), testid: "footer-terms-link" },
    { label: f.links.compliance, href: "#compliance" },
  ];

  return (
    <footer
      data-testid="site-footer"
      className="border-t border-border bg-card/30"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="grid gap-10 md:grid-cols-4">
          <div className="md:col-span-1">
            <div className="flex items-center gap-2">
              <img
                src="/logo.ico"
                alt="SecureMaintPro"
                className="h-8 w-8 rounded-sm object-contain"
              />
              <span className="font-heading text-lg font-bold tracking-tight">
                SecureMaintPro
              </span>
            </div>
            <p className="mt-4 max-w-xs text-sm leading-relaxed text-muted-foreground">
              {f.tagline}
            </p>
            <a
              href="https://www.linkedin.com/company/securemaintpro/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-linkedin-link"
              aria-label="LinkedIn"
              className="mt-5 inline-flex h-9 w-9 items-center justify-center rounded-sm border border-border bg-card text-muted-foreground transition-colors hover:border-blue-600/50 hover:text-blue-500"
            >
              <Linkedin className="h-4 w-4" strokeWidth={2} />
            </a>
          </div>

          <div>
            <h4 className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
              {f.product}
            </h4>
            <ul className="mt-4 space-y-2.5">
              {product.map((l, i) => (
                <li key={i}>
                  <a
                    href={l.href}
                    onClick={(e) => scrollTo(e, l.href)}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
              {f.resources}
            </h4>
            <ul className="mt-4 space-y-2.5">
              {resources.map((l, i) => (
                <li key={i}>
                  <a
                    href={l.href}
                    onClick={(e) => scrollTo(e, l.href)}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
              {f.legal}
            </h4>
            <ul className="mt-4 space-y-2.5">
              {legal.map((l, i) => (
                <li key={i}>
                  {l.action ? (
                    <button
                      onClick={l.action}
                      data-testid={l.testid}
                      className="text-left text-sm text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                    >
                      {l.label}
                    </button>
                  ) : l.href ? (
                    <a
                      href={l.href}
                      onClick={(e) => scrollTo(e, l.href)}
                      className="text-sm text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                    >
                      {l.label}
                    </a>
                  ) : (
                    <span className="text-sm text-muted-foreground hover:text-foreground transition-colors cursor-pointer">
                      {l.label}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-12 flex flex-col gap-2 border-t border-border pt-8 sm:flex-row sm:items-center sm:justify-between">
          <p className="font-mono text-xs text-muted-foreground">
            © {new Date().getFullYear()} SecureMaintPro. {f.rights}
          </p>
          {f.version && (
            <p
              data-testid="footer-version"
              className="font-mono text-xs text-muted-foreground/70"
            >
              {f.version}
            </p>
          )}
        </div>
      </div>
    </footer>
  );
};

export default Footer;
